// The frame each page has: its route's `handle.chrome` picks it. Owners' pages are in the owners' frame; a public page
// (the front page, Help, the sample) has the guest frame when no one's signed in; pairing has the focused frame. Focus
// moves to the page as it changes, and the scroll comes back with Back.
import type { ReactNode } from 'react';
import { Navigate, Outlet, ScrollRestoration, useLocation, useMatches, useParams } from 'react-router';
import { useAuth } from '../auth';
import { useFocusOnNavigation } from '../nav';
import { AppFrame } from './AppFrame';
import { FocusedFrame, GuestFrame } from './Frames';

/** What a route asks of the frame. */
export type Chrome = 'standard' | 'immersive' | 'focused' | 'public' | 'publicImmersive';

export interface FrameHandle {
  chrome?: Chrome;
}

function useChrome(): Chrome {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const chrome = (matches[i]?.handle as FrameHandle | undefined)?.chrome;
    if (chrome) {
      return chrome;
    }
  }
  return 'standard';
}

export function Root() {
  const chrome = useChrome();
  const signedIn = useAuth().session('owners') !== null;
  useFocusOnNavigation();
  let frame: ReactNode;
  if (chrome === 'focused') {
    frame = (
      <FocusedFrame>
        <Outlet />
      </FocusedFrame>
    );
  } else if (!signedIn && (chrome === 'public' || chrome === 'publicImmersive')) {
    frame = (
      <GuestFrame>
        <Outlet />
      </GuestFrame>
    );
  } else if (!signedIn) {
    // An owners' page goes to sign in: there's no frame to show yet.
    frame = <Outlet />;
  } else {
    frame = (
      <AppFrame immersive={chrome === 'immersive' || chrome === 'publicImmersive'}>
        <Outlet />
      </AppFrame>
    );
  }
  return (
    <>
      {frame}
      <ScrollRestoration />
    </>
  );
}

/** An old address: the page it moved to, keeping the address's search and fragment. */
export function Moved({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/** `/vehicles/:id/…`: a car's pages are in the garage now. */
export function MovedCar() {
  const { vehicleId = '', '*': rest = '' } = useParams();
  return <Moved to={`/garage/${vehicleId}${rest ? `/${rest}` : ''}`} />;
}

/** `/staff/…`: the staff's pages are in the admin dashboard, TUNE's items as its catalogue. */
export function MovedStaff() {
  const { '*': rest = '' } = useParams();
  const to = rest.replace(/^items(?=\/|$)/, 'catalogue');
  return <Moved to={`/admin${to ? `/${to}` : ''}`} />;
}

/** `/pair/:code`: the code goes in the address's search, as the MFD's QR code has it. */
export function MovedPairCode() {
  const { code = '' } = useParams();
  return <Navigate to={`/pair?${new URLSearchParams({ code })}`} replace />;
}
